package com.unrealmi.site

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // Only enable smooth scrolling unless the user has requested reduced motion (a best practice).
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(
                "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
                "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"
            )
            .fontSize(18.px)
            .lineHeight(1.6)
            .styleModifier {
                property("-webkit-font-smoothing", "antialiased")
                property("text-rendering", "optimizeLegibility")
            }
    }

    // Give code / label text a proper monospace stack for the "tech" labels used across the site.
    ctx.stylesheet.registerStyleBase("code, kbd, samp, pre") {
        Modifier.fontFamily(
            "JetBrains Mono", "SFMono-Regular", "SF Mono", "Menlo", "Consolas",
            "Liberation Mono", "monospace"
        )
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site.
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }
}

// ---------------------------------------------------------------------------------------------------------------------
// Shared gradients & helpers
// ---------------------------------------------------------------------------------------------------------------------

/** The primary brand gradient (indigo -> violet -> bright violet), reused for accents and buttons. */
fun brandGradient(angle: Int = 120): String =
    "linear-gradient(${angle}deg, #4821CB 0%, #7C5CFF 48%, #B568FE 100%)"

/** Applies the brand gradient as clipped text fill, producing a gradient-colored headline. */
fun Modifier.gradientText(angle: Int = 120): Modifier = styleModifier {
    property("background-image", brandGradient(angle))
    property("-webkit-background-clip", "text")
    property("background-clip", "text")
    property("-webkit-text-fill-color", "transparent")
    property("color", "transparent")
}

/** Frosted-glass surface: translucent fill + blur. Pass the palette-driven colors in. */
fun Modifier.glass(background: String, border: String): Modifier = styleModifier {
    property("background", background)
    property("backdrop-filter", "blur(14px)")
    property("-webkit-backdrop-filter", "blur(14px)")
    property("border", "1px solid $border")
}

// ---------------------------------------------------------------------------------------------------------------------
// Typography styles
// ---------------------------------------------------------------------------------------------------------------------

val HeadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(3.cssRem)
        .fontWeight(700)
        .textAlign(TextAlign.Start)
        .letterSpacing((-1).px)
        .lineHeight(1.1)
}

val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.1.cssRem)
        .fontWeight(400)
        .textAlign(TextAlign.Start)
        .lineHeight(1.7)
        .color(colorMode.toSitePalette().subtle)
}

/** Small monospace "eyebrow" label used above section titles, e.g. `// CORE CAPABILITIES`. */
val EyebrowStyle = CssStyle.base {
    Modifier
        .fontFamily("JetBrains Mono", "SFMono-Regular", "Menlo", "Consolas", "monospace")
        .fontSize(0.8.cssRem)
        .fontWeight(500)
        .letterSpacing(2.px)
        .color(colorMode.toSitePalette().brand.primary)
}

/** Section title used at the top of each landing section. */
val SectionTitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(2.cssRem)
            .fontWeight(700)
            .letterSpacing((-0.5).px)
            .lineHeight(1.15)
    }
    Breakpoint.MD { Modifier.fontSize(2.6.cssRem) }
}

// ---------------------------------------------------------------------------------------------------------------------
// Button variants
// ---------------------------------------------------------------------------------------------------------------------

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}

// ---------------------------------------------------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------------------------------------------------

/** Slow rotation of the ambient glow / conic ring behind the hero visual. */
val SpinAnim = Keyframes {
    from { Modifier.rotate(0.deg) }
    to { Modifier.rotate(360.deg) }
}

/** Gentle vertical float used on floating hero chips. */
val FloatAnim = Keyframes {
    0.percent { Modifier.translateY(0.px) }
    50.percent { Modifier.translateY((-12).px) }
    100.percent { Modifier.translateY(0.px) }
}

/** Fade + rise, used for on-load entrance of hero content. */
val RiseAnim = Keyframes {
    from { Modifier.opacity(0).translateY(18.px) }
    to { Modifier.opacity(1).translateY(0.px) }
}

/** Slow drift of the background aurora glows. */
val AuroraAnim = Keyframes {
    0.percent { Modifier.translate(0.px, 0.px).scale(1.0) }
    50.percent { Modifier.translate(4.percent, 3.percent).scale(1.1) }
    100.percent { Modifier.translate(0.px, 0.px).scale(1.0) }
}

/** Pulsing opacity for the "live" status dot. */
val PulseAnim = Keyframes {
    0.percent { Modifier.opacity(1).scale(1.0) }
    50.percent { Modifier.opacity(0.4).scale(0.85) }
    100.percent { Modifier.opacity(1).scale(1.0) }
}
